import styled, { css } from "styled-components";
import { borderRadius, color, highContrastStyle, snap, units } from "@m/alchemy-theme";
export const themedInputBackground = (props) => props.backgroundVariant === "white" || props.theme.aui.defaults.colorScheme === "alchemyPanel"
    ? "#fff"
    : color("canvas", { lighten: 0.012 });
/* extra 8px needed for FieldAdornment padding, 4px needed because of rounded corners */
export const BASE_INPUT_PADDING = units(2, true);
export const inputHoverRing = (palette = "interactiveConstructive") => css `
  outline: 1px solid ${color("base", { palette })};
  outline-offset: -1px;
  @media (forced-colors) {
    outline-color: -webkit-focus-ring-color;
    outline-color: Highlight;
  }
`;
export const inputFocusRing = (palette = "interactiveConstructive") => css `
  ${inputHoverRing(palette)};
  box-shadow: 0px 0px 0px 1px ${color("base", { palette })} inset;
`;
export const inputTransition = `transition: 0.2s ease-in-out box-shadow;`;
export const inputStateCSS = (props) => css `
  border: 1px solid ${color("inputBorder")};
  background-color: ${props.backgroundColor ?? highContrastStyle(color("container"), themedInputBackground)};
  background-clip: padding-box, border-box;
  background-origin: padding-box, border-box;
  ${inputTransition};

  ${props.disabled &&
    css `
    border-color: ${color("inputBorder")};
    opacity: ${highContrastStyle(0.35, 0.7)};
    cursor: not-allowed;
    &::before,
    &::after {
      border-color: ${color("inputBorder")};
    }
  `};

  ${props.hasStatus &&
    css `
    border-color: ${color("base")};
    &::after {
      border-color: ${color("base")};
    }
  `};

  /* Not :read-only so this doesn't get applied to divs using these styles (like Autosuggest MultiSelect) */
  &[readonly] {
    border-color: transparent;
    background: none;
  }

  ${(props.hasStatus || props.hasFocus) &&
    css `
    border-color: ${color("base")};
    &::after {
      border-color: ${color("base")};
    }
  `};

  &:hover:enabled {
    ${inputHoverRing(props.palette)}
  }

  &:focus-within:not([readonly]),
  &:focus:not([readonly]) {
    ${inputFocusRing(props.palette)};
  }
`;
export const Input = styled.input `
  ${({ sizeVariant = "default", ...props }) => {
    // TODO: This is not a great pattern. Either the inputs should all be wrapped in a theme provider
    // (may be too much overhead) OR we solve this better with css variables and an updated approach to theming
    const colorScheme = props.theme.aui.defaults.colorScheme === "alchemyLight" ? "alchemyPanel" : "alchemyLight";
    return css `
      ${inputStateCSS}
      font-size: var(--font-size, 14px);
      min-height: var(--input-height, 32px);
      font-family: inherit;
      appearance: none;
      box-shadow: none;
      color: ${color("typeBody", { colorScheme })};
      min-width: ${snap(120)};
      max-width: 100%;
      ${props.isFullWidth && `display: block; width: 100%;`};
      height: 100%;
      width: 100%;
      position: relative;
      line-height: 1;
      text-align: left;
      padding-left: max(
        calc(
          ${props.startAdornmentWidth ? `${props.startAdornmentWidth}px` : `var(--start-adornment-width, 0px)`} - 4px
        ),
        ${BASE_INPUT_PADDING}px
      );
      padding-right: max(
        calc(
          ${props.endAdornmentWidth
        ? `${props.endAdornmentWidth}px`
        : `var(--end-adornment-width, ${BASE_INPUT_PADDING}px)`} - 4px
        ),
        ${BASE_INPUT_PADDING}px
      );

      &::placeholder {
        color: ${color("typeMeta", { colorScheme })};
        font-style: ${sizeVariant === "default" ? "italic" : "normal"};
        font-weight: ${["medium", "large"].includes(sizeVariant) ? "bold" : "normal"};
      }

      [data-whatintent="touch"] &,
      [data-whatintent="mouse"] & {
        -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
        -webkit-tap-highlight-color: transparent;
        outline: none;
      }

      border-radius: ${borderRadius("full")};
    `;
}}
`;
export const InputWrapper = styled.div `
  min-height: 24px;
  position: relative;
  max-width: 100%;
  min-width: 0;
  display: inline-block;
  ${({ isFullWidth }) => isFullWidth && `display: block; width: 100%;`};

  &:hover input:enabled {
    ${(props) => inputHoverRing(props.palette)};
  }
`;
